import React, { useEffect, useState } from 'react'
import { Alert, Col, Row, Select, Space, Spin, Tooltip, message, notification } from 'antd'
import { CollapseHeader, CollapseRules, UserActions } from './components'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GENERAL, ROLES } from 'modules/core/constants'
import {
  generateMessageError,
  generateSelectOptions,
  isUserAllowed,
  sendUserDataToGTM,
} from 'modules/core/utils'
import { connect } from 'react-redux'
import EmptyScreen from 'modules/core/components/EmptyScreen/EmptyScreen'
import Button from 'modules/core/components/Button/Button'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import configuration from 'modules/configuration'
import planning from 'modules/planning'
import moment from 'moment'
import ProjectionUploadModal from 'modules/core/components/ProjectionUploadModal/ProjectionUploadModal'
import login from 'modules/login'

import './RuleComponent.scss'
import PropTypes from 'prop-types'
import Typography from 'modules/core/components/Typography/Typography'

const {
  SETTINGS__PROJECTION_ELEMENTS__BUSINESS_RULES__ABM,
  SETTINGS__PROJECTION_ELEMENTS__VARIABLES__ABM,
} = ROLES

const RuleComponent = ({
  visible,
  globalProjectionId,
  type,
  onRefetch,
  applyRule,
  unApplyRule,
  disabled = false,
  ruleList,
  downloadVariablesTemplateXLS,
  periodList,
  uploadVariablesFileXLS,
  fetchRulesList,
  typeModule = GENERAL.MODULES_KEYS.MODULE_PLANNING,
  moduleId = 0,
  extraParams = {},
  setShowAlert,
  applyUnappluMassive,
  extraDataApplyUnapply,
  setApplyRuleB,
  loggedUser,
}) => {
  const [isScreenLoading, setIsScreenLoading] = useState(true)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [selectRule, setSelectRule] = useState([])
  const [action, setAction] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  let { periodId } = useParams()
  let history = useHistory()
  const { t } = useTranslation()

  const dataPeriod = periodList.find((el) => el.id === parseInt(periodId))

  useEffect(() => {
    if (visible) {
      fetchRulesList({ period_id: periodId, type, pk: globalProjectionId, ...extraParams }).then(
        () => setIsScreenLoading(false),
      )
    }
  }, [fetchRulesList, dataPeriod, visible, periodId, type, globalProjectionId])

  const userCanRuleCreate = isUserAllowed(SETTINGS__PROJECTION_ELEMENTS__BUSINESS_RULES__ABM)
  const userCanVariableCreate = isUserAllowed(SETTINGS__PROJECTION_ELEMENTS__VARIABLES__ABM)

  const onSuccessUploadFile = ({ setIsLoading, fetchRulesList }) => {
    notification.success({
      message: t('FEEDBACK_VERY_GOOD'),
      description: t('FEEDBACK_LOAD_DATA_SUCCESS'),
    })
    setIsLoading(true)
    fetchRulesList({ period_id: periodId, type, pk: globalProjectionId }).then(() =>
      setIsLoading(false),
    )
    sendUserDataToGTM(loggedUser, 'Reglas', 'Cargar reglas', 'Carga')
  }

  const onFailUploadFile = (error) => {
    notification.error({
      key: 'error_file',
      message: t('FEEDBACK_DEFAULT_ERROR'),
      description: generateMessageError(error),
      duration: 0,
    })
  }

  const renderAlert = () => (
    <Alert
      showIcon
      closable
      type="info"
      description={t('PLANNING_RULE_KEEP_DATA_INFORMATIVE_ALERT_DESCRIPTION')}
    />
  )

  const renderNoDataScreen = () => (
    <EmptyScreen
      image={emptyStateImage}
      title={t('CONFIG_FORMULAS_EMPTY_SCREEN_TITLE')}
      description={t('CONFIG_FORMULAS_EMPTY_SCREEN_DESCRIPTION')}
      footer={
        <Button.Primary
          onClick={() => history.push('/configuracion/herramientas/')}
          title="ACTION_GO_TO_CONFIGURE"
        />
      }
    />
  )

  const getData = () => {
    if (searchValue) {
      return ruleList.values.filter((el) =>
        el.name.toLowerCase().includes(searchValue.toLowerCase()),
      )
    }
    return ruleList.values
  }

  const handleApplyUnappluMassive = () => {
    setIsSaving(true)
    setApplyRuleB(true)
    applyUnappluMassive({ ...extraDataApplyUnapply, action, business_rule_ids: selectRule })
      .then(() =>
        fetchRulesList({ period_id: periodId, type, pk: globalProjectionId, ...extraParams }).then(
          () => {
            message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
            setSelectRule([])
            setAction('')
            setIsSaving(false)
          },
        ),
      )
      .catch((error) => {
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
        setAction('')
        setSelectRule([])
        setIsSaving(false)
      })
  }

  const renderApplyUnapplyMassive = () => {
    return (
      <Space direction="vertical">
        <Space direction="horizontal">
          <Typography.Body level={2}>
            {t('TYPOGRAPHY_APPLY_UNAPPLY_MASSIVE_RULES_TEXT')}
          </Typography.Body>
          <Tooltip title={t('INFORMATIVE_TOOLTIP_APPLY_UNAPPLY_MASSIVE_RULES_TITLE')}>
            <Typography.Icon icon={InfoCircleOutlined} />
          </Tooltip>
        </Space>
        <Space direction="horizontal">
          <Select
            placeholder="Seleccionar"
            options={generateSelectOptions({
              options: [
                { id: 'apply', name: t('ACTION_APPLY') },
                { id: 'unapply', name: t('ACTION_UNAPPLY') },
              ],
            })}
            onChange={(val) => setAction(val)}
            style={{ width: 200 }}
            defaultValue={action || undefined}
            key={action || undefined}
          />
          <Button.Primary
            title="ACTION_SAVE"
            disabled={!action || selectRule.length === 0 || isSaving}
            onClick={() => handleApplyUnappluMassive()}
            loading={isSaving}
          />
        </Space>
      </Space>
    )
  }

  return (
    <Spin spinning={isScreenLoading}>
      <Row gutter={[24, 24]}>
        {ruleList.count === 0 && <Col span={24}>{renderNoDataScreen()}</Col>}
        {ruleList.count > 0 && (
          <>
            <Col span={24}>
              <UserActions
                disabled={!userCanVariableCreate || disabled}
                onClick={() => setShowUploadModal(true)}
                setSearchValue={setSearchValue}
              />
            </Col>
            <Col span={24}>{renderAlert()}</Col>
            <Col span={24}>{renderApplyUnapplyMassive()}</Col>
            <Col span={24}>
              <CollapseHeader />
              <CollapseRules
                ruleList={getData()}
                canCreate={userCanRuleCreate || userCanVariableCreate}
                onRefetch={onRefetch}
                onRefetchRules={() =>
                  fetchRulesList({ period_id: periodId, type, pk: globalProjectionId })
                }
                applyRule={applyRule}
                unApplyRule={unApplyRule}
                disabled={disabled}
                typeModule={typeModule}
                moduleId={moduleId}
                setShowAlert={setShowAlert}
                selectRule={selectRule}
                setSelectRule={setSelectRule}
                action={action}
              />
            </Col>
          </>
        )}
      </Row>
      <ProjectionUploadModal
        title={t('CONFIG_VAR_UPLOAD_VALUES_MODAL_TITLE')}
        visible={showUploadModal}
        onCancel={() => setShowUploadModal(false)}
        uploadFile={(fileList) =>
          uploadVariablesFileXLS(periodId, fileList, type, globalProjectionId)
        }
        onSuccessUpload={() =>
          onSuccessUploadFile({
            setIsLoading: setIsScreenLoading,
            fetchRulesList,
          })
        }
        onFailUpload={(error) => onFailUploadFile(error)}
        downloadTemplateFile={() =>
          downloadVariablesTemplateXLS(
            periodId,
            t('PLANNING_TEMPLATE_UPLOAD_VARIABLES_VALUES', {
              periodName: dataPeriod.name,
              date: moment().format('lll'),
            }),
            { type, pk: globalProjectionId },
          )
        }
      />
    </Spin>
  )
}

const mapStateToProps = (state) => ({
  periodList: planning.selectors.getPeriodList(state),
  ruleList: configuration.selectors.getRulesList(state),
  loggedUser: login.selectors.getWhoAmI(state),
})

const mapDispatchToProps = {
  downloadVariablesTemplateXLS: planning.actions.downloadVariablesTemplateXLS,
  uploadVariablesFileXLS: planning.actions.uploadVariablesFileXLS,
  fetchRulesList: configuration.actions.fetchRulesList,
}

RuleComponent.propTypes = {
  visible: PropTypes.bool,
  globalProjectionId: PropTypes.any,
  type: PropTypes.string,
  onRefetch: PropTypes.func,
  applyRule: PropTypes.func,
  unApplyRule: PropTypes.func,
  disabled: PropTypes.bool,
  ruleList: PropTypes.shape({
    values: PropTypes.array,
    count: PropTypes.number,
  }),
  downloadVariablesTemplateXLS: PropTypes.func,
  periodList: PropTypes.array,
  uploadVariablesFileXLS: PropTypes.func,
  fetchRulesList: PropTypes.func,
  typeModule: PropTypes.string,
  moduleId: PropTypes.number,
  extraParams: PropTypes.object,
  setShowAlert: PropTypes.func,
  applyUnappluMassive: PropTypes.func,
  extraDataApplyUnapply: PropTypes.object,
  setApplyRuleB: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(RuleComponent)
